import axios, { type AxiosInstance, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/store/authStore'

// ─── Axios Instance ───────────────────────────────────────────────────────────
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? 'http://localhost:8000/api/v1',
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

// ─── Request Interceptor: attach Bearer token ────────────────────────────────
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error),
)

// ─── Token Refresh Logic ─────────────────────────────────────────────────────
let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (error: unknown) => void
}> = []

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else if (token) {
      resolve(token)
    }
  })
  failedQueue = []
}

// ─── Response Interceptor ────────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }
    const status: number | undefined = error.response?.status

    // ── 401: try token refresh then retry ────────────────────────────────────
    if (status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token) => {
              if (originalRequest.headers) {
                originalRequest.headers['Authorization'] = `Bearer ${token}`
              }
              resolve(api(originalRequest))
            },
            reject,
          })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const { refreshToken, setAccessToken, logout } = useAuthStore.getState()
        if (!refreshToken) throw new Error('No refresh token')

        const res = await axios.post<{ accessToken: string }>(
          `${api.defaults.baseURL}/auth/refresh-token`,
          { refreshToken },
        )
        const newAccessToken = res.data.accessToken
        setAccessToken(newAccessToken)
        processQueue(null, newAccessToken)
        originalRequest.headers = {
          ...originalRequest.headers,
          Authorization: `Bearer ${newAccessToken}`,
        }
        return api(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        useAuthStore.getState().logout()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // ── 400: Doğrulama / iş kuralı hatası ────────────────────────────────────
    if (status === 400) {
      const data = error.response?.data
      // Prefer specific business error, then field-level validation messages, then generic title
      const fieldErrors = data?.errors
        ? (Object.values(data.errors) as string[][])
            .flat()
            .filter((m: string) => !m.startsWith('$') && m !== 'The command field is required.')
            .join(', ')
        : null
      const msg = data?.error ?? fieldErrors ?? data?.title ?? 'Geçersiz işlem.'
      showToast(msg, 'error')
    }

    // ── 403: Yetkisiz erişim ──────────────────────────────────────────────────
    if (status === 403) {
      showToast('Bu işlem için yetkiniz bulunmamaktadır.', 'error')
    }

    // ── 404: Kayıt bulunamadı ─────────────────────────────────────────────────
    if (status === 404) {
      showToast('İstenen kayıt bulunamadı.', 'warning')
    }

    // ── 500+: Sunucu hatası ───────────────────────────────────────────────────
    if (status !== undefined && status >= 500) {
      showToast('Sunucu hatası. Lütfen daha sonra tekrar deneyin.', 'error')
    }

    return Promise.reject(error)
  },
)

// ─── Toast helper (decoupled – avoids circular dep with toast store) ──────────
function showToast(message: string, type: 'error' | 'warning' | 'success' = 'error') {
  // Dispatch a custom DOM event that the Toast provider listens to.
  window.dispatchEvent(
    new CustomEvent('sporokulu:toast', { detail: { message, type } }),
  )
}

export default api

// ─── Typed API helpers ────────────────────────────────────────────────────────
export const apiGet = <T>(url: string, config?: AxiosRequestConfig) =>
  api.get<T>(url, config).then((r) => r.data)

export const apiPost = <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
  api.post<T>(url, data, config).then((r) => r.data)

export const apiPut = <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
  api.put<T>(url, data, config).then((r) => r.data)

export const apiPatch = <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
  api.patch<T>(url, data, config).then((r) => r.data)

export const apiDelete = <T>(url: string, config?: AxiosRequestConfig) =>
  api.delete<T>(url, config).then((r) => r.data)
